import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  ChevronRight,
  CreditCard,
  Gauge,
  LogOut,
  Moon,
  Settings,
  ShoppingBag,
  Sparkles,
  Sun,
  User,
  Users,
  Wallet,
  HardDrive,
  KeyRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { useTheme } from "@/components/dashboard/ThemeProvider";
import { InstanceBrandLockup } from "@/components/instance/InstanceBrandingProvider";
import { useAuth } from "@/lib/auth-context";
import { useDashboardLive } from "@/lib/dashboard-live";
import { PRODUCT_HOME } from "@/lib/product";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import { cn } from "@/lib/utils";

const productNav = [
  { to: PRODUCT_HOME, label: "Wallet", hint: "Credits", icon: Wallet },
  { to: "/run-workloads", label: "Run workload", hint: "Compute", icon: Sparkles },
  { to: "/storage", label: "Storage", hint: "Files", icon: HardDrive },
  { to: "/store", label: "Store", hint: "Capacity", icon: ShoppingBag },
  { to: "/org", label: "Org", hint: "Admin", icon: Users },
  { to: "/crm", label: "Manage", hint: "Health", icon: Gauge },
] as const;

type NavItem = {
  to: string;
  label: string;
  hint: string;
  icon: LucideIcon;
};

const navAliases: Record<string, string[]> = {
  "/run-workloads": ["/run-job"],
  "/crm": ["/compute-resource-management"],
  "/store": ["/pooled-compute"],
};

const settingsNav = [
  { href: "#account", label: "Account", icon: User },
  { href: "#workspace", label: "Workspace", icon: Users },
  { href: "#billing", label: "Billing", icon: CreditCard },
  { href: "#providers", label: "Providers", icon: KeyRound },
  { href: "#preferences", label: "Preferences", icon: Bell },
  { href: "#session", label: "Session", icon: LogOut },
] as const;

export function AppSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [activeSettingsHash, setActiveSettingsHash] = useState("#account");
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const live = useDashboardLive();
  const syncText = live.fetchedAt
    ? `${Math.max(0, Math.floor((Date.now() - new Date(live.fetchedAt).getTime()) / 1000))}s ago`
    : "not synced";
  const nominal = !live.error;
  const settingsMode = pathname === "/settings";

  useEffect(() => {
    if (!settingsMode || typeof window === "undefined") return;
    const syncHash = () => {
      const searchSection = new URLSearchParams(window.location.search).get("section") || "";
      const raw = window.location.hash || (searchSection ? `#${searchSection}` : "#account");
      setActiveSettingsHash(raw === "#pro" ? "#providers" : raw);
    };
    syncHash();
    window.addEventListener("hashchange", syncHash);
    return () => window.removeEventListener("hashchange", syncHash);
  }, [settingsMode]);

  return (
    <>
      <aside className="genrl-sidecar hidden md:flex">
        <Link to={PRODUCT_HOME} className="genrl-brand-lockup">
          <InstanceBrandLockup />
        </Link>

        <nav className="genrl-nav-stack" aria-label={settingsMode ? "Settings sections" : "Product"}>
          {settingsMode ? (
            <>
              {settingsNav.map((item) => (
                <SettingsAnchor
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  label={item.label === "Providers" ? "Connected providers" : item.label}
                  active={activeSettingsHash === item.href}
                />
              ))}
            </>
          ) : (
            <NavList items={productNav as readonly NavItem[]} pathname={pathname} />
          )}
        </nav>

        {settingsMode && (
          <button
            type="button"
            onClick={() => navigate({ to: PRODUCT_HOME })}
            className="genrl-soft-button"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to app
          </button>
        )}

        <div className="genrl-sidecar-footer">
          <div className="genrl-status-card">
            <span className={cn("genrl-status-dot", nominal ? "genrl-status-dot-ok" : "genrl-status-dot-warn")} />
            <span>
              <strong>{nominal ? "Ready" : "Checking"}</strong>
              <small>Synced {syncText}</small>
            </span>
          </div>

          <button
            onClick={toggle}
            className="genrl-soft-button"
            type="button"
            aria-label="Toggle colour mode"
          >
            {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            {theme === "dark" ? "Dark" : "Light"}
          </button>

          {user && (
            <div className="genrl-user-card">
              <span>{user.name.slice(0, 2).toUpperCase()}</span>
              <div>
                <strong>{user.name}</strong>
                <small>{user.email}</small>
              </div>
              <button
                onClick={async () => {
                  await logout();
                  if (!isWalletCloudMode()) navigate({ to: "/login" });
                }}
                title="Sign out"
                aria-label="Sign out"
                type="button"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}

          {!settingsMode && (
            <Link to="/settings" className="genrl-settings-link">
              <Settings className="h-4 w-4" />
              Settings
              <ChevronRight className="ml-auto h-4 w-4" />
            </Link>
          )}
        </div>
      </aside>

      {settingsMode ? (
        <MobileSettingsNav activeHash={activeSettingsHash} />
      ) : (
        <MobileDock pathname={pathname} />
      )}
    </>
  );
}

function MobileSettingsNav({ activeHash }: { activeHash: string }) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 flex min-h-[68px] overflow-x-auto border-t border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-[max(8px,env(safe-area-inset-left))] pb-[env(safe-area-inset-bottom)] md:hidden"
      aria-label="Settings sections"
    >
      {settingsNav.map((item) => {
        const Icon = item.icon;
        const active = activeHash === item.href;
        return (
          <a
            key={item.href}
            href={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex min-h-11 min-w-[92px] flex-1 flex-col items-center justify-center gap-1 border-x border-transparent px-2 text-[9px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]",
              active && "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-fg)]",
            )}
          >
            <Icon className="h-4 w-4" />
            <span>{item.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

function SettingsAnchor({
  href,
  icon: Icon,
  label,
  active,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
}) {
  return (
    <a href={href} className={cn("genrl-nav-item", active && "genrl-nav-item-active")}>
      <Icon className="h-5 w-5" />
      <span>
        <strong>{label}</strong>
        <small>Settings</small>
      </span>
    </a>
  );
}

function NavList({ items, pathname }: { items: readonly NavItem[]; pathname: string }) {
  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        const active = pathname === item.to || (navAliases[item.to] || []).includes(pathname);
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn("genrl-nav-item", active && "genrl-nav-item-active")}
          >
            <Icon className="h-5 w-5" />
            <span>
              <strong>{item.label}</strong>
              <small>{item.hint}</small>
            </span>
          </Link>
        );
      })}
    </>
  );
}

function MobileDock({ pathname }: { pathname: string }) {
  return (
    <nav className="genrl-mobile-dock md:hidden" aria-label="Product">
      {productNav.map((item) => {
        const Icon = item.icon;
        const active = pathname === item.to || (navAliases[item.to] || []).includes(pathname);
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn("genrl-mobile-dock-item", active && "genrl-mobile-dock-item-active")}
            aria-label={item.label}
          >
            <Icon className="h-5 w-5" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
